import React, { useEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Eraser, ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";
import {
  saveSignatureBackup,
  loadSignatureBackup,
  signatureBackupTimestamp,
} from "@/lib/signatureBackup";
import { useWizard } from "@/context/WizardContext";

/**
 * SignaturePadField — finger/pen-drawn signature with bulletproof persistence.
 *
 * Lifecycle requirement
 * ---------------------
 * The RN cannot return to the client's home for ~60 days. A lost signature
 * = a lost visit. So this component:
 *   1. Auto-saves a PNG data URL after EVERY stroke (`onEnd`).
 *   2. Writes to BOTH the wizard state (via `onChange`) AND a parallel
 *      backup keyed independently (`signatureBackup.js`) — surviving wizard
 *      resets and "Discard draft" actions.
 *   3. On mount, if the prop value is empty but the backup has data,
 *      auto-restores so accidental wipes do not destroy the capture.
 *   4. Shows a visible "Saved at HH:MM:SS" badge after each stroke so the
 *      nurse has positive confirmation.
 *
 * Tablet-first
 * ------------
 *   - Large default height (180px) for finger drawing
 *   - Native pointer events (works with finger AND Apple Pencil/stylus)
 *   - "Clear" only triggers an explicit reset; accidental taps don't lose work
 */
export default function SignaturePadField({
  role,                    // "rn" | "client" | "agency_rep" | "pcw" — used as backup key
  label = "Signature",
  testid,
  value,                   // current PNG data URL (from wizard state)
  onChange,                // (dataUrl: string | "") => void
  required = true,
  height = 180,
}) {
  const padRef = useRef(null);
  const wrapperRef = useRef(null);
  const [savedAt, setSavedAt] = useState(null);
  const [restored, setRestored] = useState(false);
  const [width, setWidth] = useState(600);
  const { data, setData } = useWizard();

  // The WizardContext on mount runs hydrateSignatureBackup() which restores
  // any backed-up signatures into wizard state AND tags the roles it touched
  // on `signatures._restored_from_backup`. Read that flag once on mount —
  // we own its lifecycle from here on (clear it after acknowledging).
  const wasRestoredFromBackup = useMemo(() => {
    const flag = data?.signatures?._restored_from_backup;
    return Array.isArray(flag) && flag.includes(role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  // Watch wrapper width so the canvas fills the container responsively.
  // Drawing into a stretched canvas distorts strokes, so we resize the
  // backing buffer when the wrapper resizes.
  useEffect(() => {
    if (!wrapperRef.current) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[0].contentRect.width);
      if (w && Math.abs(w - width) > 4) setWidth(w);
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate canvas from the prop value (parent state) OR the independent
  // backup. Backup wins ONLY if parent state is empty (i.e. parent was reset
  // but the signature backup survived).
  useEffect(() => {
    if (!padRef.current) return;
    const fromParent = value || "";
    const fromBackup = loadSignatureBackup(role) || "";
    const target = fromParent || fromBackup;
    if (!target) return;

    // Push the data URL into the canvas. fromDataURL handles base64 PNG cleanly.
    try {
      padRef.current.fromDataURL(target, { width, height });
      const ts = signatureBackupTimestamp(role);
      if (wasRestoredFromBackup) {
        // WizardContext.hydrateSignatureBackup() injected this PNG into wizard
        // state on boot — show the "Restored from backup" chip so the nurse
        // knows the recovery happened, then clear the flag so it doesn't
        // persist past this session.
        setRestored(true);
        setSavedAt(ts);
        setData((d) => {
          const flag = d?.signatures?._restored_from_backup;
          if (!Array.isArray(flag) || !flag.includes(role)) return d;
          const next = flag.filter((r) => r !== role);
          const sigs = { ...(d.signatures || {}) };
          if (next.length === 0) delete sigs._restored_from_backup;
          else sigs._restored_from_backup = next;
          return { ...d, signatures: sigs };
        });
      } else if (!fromParent && fromBackup) {
        // Parent state was empty but backup found it (e.g. a refresh after
        // wizard state was wiped but before WizardContext ran).
        onChange?.(fromBackup);
        setRestored(true);
        setSavedAt(ts);
      } else if (fromParent) {
        setSavedAt(ts);
      }
    } catch {
      /* malformed data URL — leave canvas blank */
    }
    // We intentionally only run this when role/width/height change, not on
    // every parent re-render, otherwise typing a name elsewhere would
    // re-render and flicker the canvas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, width, height]);

  const persist = () => {
    if (!padRef.current) return;
    if (padRef.current.isEmpty()) {
      onChange?.("");
      setSavedAt(null);
      return;
    }
    // toDataURL is synchronous; PNG keeps transparent strokes nicely.
    const dataUrl = padRef.current.toDataURL("image/png");
    onChange?.(dataUrl);
    saveSignatureBackup(role, dataUrl);
    setSavedAt(new Date().toISOString());
    setRestored(false);
  };

  const clear = () => {
    if (!padRef.current) return;
    padRef.current.clear();
    onChange?.("");
    setSavedAt(null);
    setRestored(false);
    // We intentionally do NOT clear the backup here — only an explicit
    // wizard `reset()` should wipe the backup. If the nurse hits Clear
    // by accident, the backup still has the prior capture and the next
    // stroke will overwrite it.
  };

  const status = useMemo(() => {
    if (!savedAt) {
      return { kind: "empty", label: required ? "Signature required" : "Signature optional" };
    }
    const t = new Date(savedAt);
    const hh = t.toLocaleTimeString();
    return { kind: "saved", label: `Saved · ${hh}` };
  }, [savedAt, required]);

  return (
    <div className="space-y-2" data-testid={`${testid}-field`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold text-[#0B1E3A]">
          {label}
          {required && <span className="text-[#c05746]">*</span>}
        </div>
        <div className="flex items-center gap-2">
          {restored && (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A3D79] bg-[#E7F1FB] px-2 py-0.5 rounded-full"
              data-testid={`${testid}-restored`}
              title="Signature recovered from backup after wizard state was wiped."
            >
              <RefreshCw className="w-3 h-3" /> Restored from backup
            </span>
          )}
          {status.kind === "saved" ? (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A3D79]"
              data-testid={`${testid}-saved`}
            >
              <ShieldCheck className="w-3 h-3" /> {status.label}
            </span>
          ) : (
            <span
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8A6B00]"
              data-testid={`${testid}-empty`}
            >
              <AlertTriangle className="w-3 h-3" /> {status.label}
            </span>
          )}
        </div>
      </div>

      <div
        ref={wrapperRef}
        className="relative rounded-xl border-2 border-[#D6E3EF] bg-white overflow-hidden focus-within:border-[#0A3D79] transition-colors"
        style={{ height }}
        data-testid={`${testid}-canvas-wrap`}
      >
        <SignatureCanvas
          ref={padRef}
          penColor="#0B1E3A"
          minWidth={1.2}
          maxWidth={2.4}
          velocityFilterWeight={0.6}
          onEnd={persist}
          canvasProps={{
            width,
            height,
            className: "block touch-none",
            "data-testid": `${testid}-canvas`,
          }}
        />
        {/* Faint guide line so the user knows where to sign */}
        <div
          aria-hidden="true"
          className="absolute left-4 right-4 bottom-7 border-b border-dashed border-[#C1D7EF] pointer-events-none"
        />
        <div
          aria-hidden="true"
          className="absolute left-4 bottom-2 text-[10px] uppercase tracking-[0.18em] text-[#8FA6BE] font-semibold pointer-events-none"
        >
          Sign above the line
        </div>
      </div>

      <div className="flex justify-between items-center pt-0.5">
        <span className="text-[11.5px] text-[#3A5275]">
          Auto-saves after every stroke. Backup persists across wizard resets.
        </span>
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center gap-1 text-[12px] text-[#3A5275] hover:text-[#c05746] transition-colors"
          data-testid={`${testid}-clear`}
        >
          <Eraser className="w-3.5 h-3.5" /> Clear
        </button>
      </div>
    </div>
  );
}
